<template>
  <Modal
    v-if="showModal"
    :auto-opened="true"
    :show-modal="showModal"
    :centered-text="false"
    :show-close-icon="true"
    :header-divider="false"
    :image-src="`${assetsBaseUrl}/assets/images/UWG.png`"
    :max-width="800"
    @closeModal="handleCloseModal"
  >
    <template #content>
      <h2>{{ $t('advertisementConsent.modal.title') }}</h2>
      <div v-html="$t('advertisementConsent.modal.description')" class="mb-32"></div>
      <Collapser
        :collapser-title="$t('advertisementConsent.modal.collapser.title')"
        variant="secondary"
      >
        <template #collapserContent>
          <div v-html="$t('advertisementConsent.modal.collapser.content')"></div>
        </template>
      </Collapser>
    </template>
    <template #footer>
      <div class="footer-cta footer-cta--left">
        <CustomButton
          @click="handleAccept"
          :button-color="ButtonColor.PRIMARY"
          :disabled="isLoading"
        >
          {{ $t('advertisementConsent.modal.buttons.accept') }}
        </CustomButton>
        <CustomButton
          @click="handleDecline"
          :button-color="ButtonColor.SECONDARY"
          :disabled="isLoading"
        >
          {{ $t('advertisementConsent.modal.buttons.decline') }}
        </CustomButton>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ButtonColor, Collapser, CustomButton, Modal } from 'de-zurich-ui-library'
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { AdvertisementConsentService } from '@/services/AdvertisementConsentService'
import { getPartnerProxyId } from '@/utils/KeycloakUtils'

// Props
defineProps<{
  routerLoaded?: boolean
}>()

const { t } = useI18n()
const route = useRoute()
const isLoading = ref(false)
const showModal = ref(false)
const assetsBaseUrl: string = globalThis.ENV_CONFIG.ASSETS_BASE_URL.CUSTOMER_PORTAL || ''

const handleCloseModal = () => {
  showModal.value = false
}

const handleAccept = async () => {
  isLoading.value = true
  try {
    // Close the modal immediately as per requirements - regardless of BE response
    showModal.value = false

    // Send POST request to backend
    await AdvertisementConsentService.sendAdvertisementConsent()
  } catch (error) {
    console.error('Failed to send advertisement consent:', error)
  } finally {
    isLoading.value = false
  }
}

const handleDecline = () => {
  showModal.value = false
}

async function checkAdvertisementConsent(): Promise<void> {
  try {
    // Only check on overview page for authenticated users
    const partnerProxyId = getPartnerProxyId()
    if (route.path === '/ueberblick' && partnerProxyId) {
      const response = await AdvertisementConsentService.getAdvertisementConsent()
      // Show the modal if the user did not already give advertisement consent
      showModal.value = response.askForAdvertisementConsent ?? false
    }
  } catch (error) {
    console.error('Failed to check advertisement consent:', error)
  }
}

// Check consent when component is mounted and when route changes to overview
onMounted(async () => {
  await checkAdvertisementConsent()
})

// Watch for route changes to check advertisement consent on overview page
watch(
  () => route.path,
  async (newPath) => {
    if (newPath === '/ueberblick') {
      await checkAdvertisementConsent()
    }
  }
)
</script>
